/**
 * SKU Search — lets users search by SKU and add variants to the cart.
 *
 * Lookup strategy (3-tier fallback):
 *  1. /search/suggest.json — fetch candidate products
 *  2. /products/{handle}.json — check variant.sku for exact match
 *  3. /products.json?limit=250 (up to 750 products) — brute-force fallback
 */

(function () {
  const wrapper = document.getElementById('sku-search-wrapper');
  if (!wrapper) return;

  const input = wrapper.querySelector('.sku-search__input');
  const button = wrapper.querySelector('.sku-search__button');
  const resultsContainer = wrapper.querySelector('.sku-search__results');
  const messageContainer = wrapper.querySelector('.sku-search__message');

  if (!input || !button || !resultsContainer || !messageContainer) return;

  button.addEventListener('click', () => doSearch());
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      doSearch();
    }
  });

  /* ------------------------------------------------------------------ */
  /*  Search orchestration                                               */
  /* ------------------------------------------------------------------ */

  async function doSearch() {
    const sku = input.value.trim();
    if (!sku) {
      showMessage(window.skuSearchStrings.empty, 'error');
      return;
    }

    showMessage('', '');
    resultsContainer.innerHTML = '';
    button.classList.add('loading');
    button.setAttribute('disabled', true);

    try {
      let match = await findViaSuggest(sku);
      if (!match) match = await findViaProductsJson(sku);

      if (match) {
        await addToCart(match.variant.id, 1);
      } else {
        showMessage(window.skuSearchStrings.not_found, 'error');
      }
    } catch (err) {
      console.error('[sku-search]', err);
      showMessage(window.skuSearchStrings.error, 'error');
    } finally {
      button.classList.remove('loading');
      button.removeAttribute('disabled');
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Tier 1 + 2 — search/suggest → per-product variant check           */
  /* ------------------------------------------------------------------ */

  async function findViaSuggest(sku) {
    const url =
      '/search/suggest.json?q=' +
      encodeURIComponent(sku) +
      '&resources[type]=product&resources[limit]=10';

    const res = await fetch(url);
    if (!res.ok) return null;

    const data = await res.json();
    const products = data.resources?.results?.products ?? [];

    for (const p of products) {
      const match = await checkProductHandle(p.handle, sku);
      if (match) return match;
    }

    return null;
  }

  async function checkProductHandle(handle, sku) {
    const res = await fetch('/products/' + handle + '.json');
    if (!res.ok) return null;

    const data = await res.json();
    const product = data.product;
    const variant = product.variants.find(
      (v) => v.sku && v.sku.toLowerCase() === sku.toLowerCase()
    );

    return variant ? { product, variant } : null;
  }

  /* ------------------------------------------------------------------ */
  /*  Tier 3 — /products.json brute-force (max 750 products)            */
  /* ------------------------------------------------------------------ */

  async function findViaProductsJson(sku) {
    let page = 1;
    const maxPages = 3;

    while (page <= maxPages) {
      const res = await fetch('/products.json?limit=250&page=' + page);
      if (!res.ok) break;

      const data = await res.json();
      const products = data.products;
      if (!products || products.length === 0) break;

      for (const product of products) {
        const variant = product.variants.find(
          (v) => v.sku && v.sku.toLowerCase() === sku.toLowerCase()
        );
        if (variant) return { product, variant };
      }

      if (products.length < 250) break;
      page++;
    }

    return null;
  }

  /* ------------------------------------------------------------------ */
  /*  Add to cart (mirrors product-form.js pattern)                      */
  /* ------------------------------------------------------------------ */

  function addToCart(variantId, quantity) {
    const cartItems = document.querySelector('cart-items');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);

    if (cartItems) {
      formData.append(
        'sections',
        cartItems.getSectionsToRender().map((s) => s.section)
      );
    }

    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    return fetch(routes.cart_add_url, config)
      .then((r) => r.json())
      .then((response) => {
        if (response.status) {
          showMessage(response.description || response.message || window.skuSearchStrings.error, 'error');
          return;
        }

        showMessage(window.skuSearchStrings.added, 'success');

        if (cartItems) {
          cartItems.renderContents(response);
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'sku-search',
          productVariantId: variantId,
          cartData: response,
        });
      })
      .catch((err) => {
        console.error('[sku-search] add-to-cart error', err);
        showMessage(window.skuSearchStrings.error, 'error');
      });
  }

  /* ------------------------------------------------------------------ */
  /*  Helpers                                                            */
  /* ------------------------------------------------------------------ */

  function showMessage(text, type) {
    messageContainer.textContent = text;
    messageContainer.className = 'sku-search__message';
    if (type) messageContainer.classList.add('sku-search__message--' + type);
  }

})();
